// The Reporting page's model: the period (a month or a window), its social report, and the canvas layout, all read
// from the URL so a refresh, Back and a shared link restore them.
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSocialMediaReport, type TopPost } from "./useSocialMediaReport";
import { growthKeys } from "./useGrowth";
import { useWindowReports } from "./useWindowReports";
import { addDays, monthRange, rangeOf, todayRome, type DayRange } from "../utils/dayAxis";
import { postsByDay, type PostDay } from "../utils/dayMarkers";
import { parsePeriod, searchFor, WINDOW_PERIODS, windowDays, type ReportPeriod } from "../utils/period";
import { windowTopPosts } from "../utils/windowTopPosts";
import { useMediaQuery } from "@/core/http/useMediaQuery";
import { useAppStore } from "@/core/store/useAppStore";
import {
  TIER_MIN_WIDTH,
  TILE_CAP,
  autoPlace,
  countTiles,
  edgesFor as edgesOfLayout,
  panelsOf,
  parse,
  place as placeInLayout,
  remove,
  replace as replaceInLayout,
  serialize,
  tile,
  visibleLayout,
  type CanvasLayout,
  type Edge,
  type Tier,
} from "../canvas/canvasLayout";
import { DEFAULT_PANEL, isPanelId, type PanelId } from "../canvas/panels";
import type { SocialReportState } from "../canvas/ReportPanelById";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export interface ReportingViewModel {
  /** Empty while no client is selected. */
  clientId: string;
  /** A month, or the last 7 or 28 days. */
  period: ReportPeriod;
  /** Today in Europe/Rome, "YYYY-MM-DD": a window's hooks key on it, so it moves on with the day. */
  today: string;
  /** The month shown; in window mode the current month, the one Month opens. */
  year: number;
  month: number;
  periodLabel: string;
  /** The month's report has a month before to compare with; never in window mode, where each panel names its own. */
  comparesPrevious: boolean;
  updatedAt: string | null;
  setPeriod(period: ReportPeriod): void;
  setMonth(year: number, month: number): void;
  social: SocialReportState;
  /** The social report is in, so its Summary can be written. */
  hasReport: boolean;
  isRefreshing: boolean;
  refreshingPlatform: string | null;
  refreshReport(): void;
  refreshPlatform(platform: "facebook" | "instagram" | "tiktok"): void;
  /** The period's posts by Rome day, the pips on the day charts. */
  postDays: PostDay[];
  /** The period's top posts: the month report's, or in window mode the posts published in the window; null while
   *  there is nothing to rank. */
  topPosts: TopPost[] | null;

  /** The window's width tier: a phone lists the panels, a tablet and a desktop draw the rail and canvas. */
  tier: Tier;
  /** How many tiles this tier shows. */
  cap: number;
  /** The whole canvas, as the URL has it. Null is an empty canvas. */
  layout: CanvasLayout | null;
  /** The part of it this tier shows: all of it, except a tablet's first two tiles of a larger layout. */
  shown: CanvasLayout | null;
  /** Every panel in the layout: expanded on a phone, ringed in the rail. */
  openPanels: PanelId[];
  /** Panels in the layout past this tier's cap, in reading order. */
  hiddenPanels: PanelId[];
  /** There is room for another panel at this tier. */
  canAdd: boolean;
  /** The edges a new tile can land on beside this panel's tile. */
  edgesFor(panel: PanelId): Edge[];
  /** Fills an empty canvas. */
  open(panel: PanelId): void;
  place(panel: PanelId, target: PanelId, edge: Edge): void;
  /** Opens the panel in the next free spot, a desktop's click with no tile picked and a phone's tap alike. */
  add(panel: PanelId): void;
  /** Puts `panel` in `target`'s tile instead, the split unchanged. */
  replace(target: PanelId, panel: PanelId): void;
  close(panel: PanelId): void;
}

export function useReportingView(): ReportingViewModel {
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id ?? "";
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const now = new Date();
  const period = useMemo(() => parsePeriod(query, new Date()), [query]);
  const today = todayRome(now);
  // A window shows no month; Month opens on the current one.
  const year = period.kind === "month" ? period.year : now.getFullYear();
  const month = period.kind === "month" ? period.month : now.getMonth() + 1;
  const windowed = period.kind === "window" ? period.period : null;

  const desktop = useMediaQuery(`(min-width: ${TIER_MIN_WIDTH.desktop}px)`);
  const tablet = useMediaQuery(`(min-width: ${TIER_MIN_WIDTH.tablet}px)`);
  const tier: Tier = desktop ? "desktop" : tablet ? "tablet" : "phone";
  const cap = TILE_CAP[tier];

  const canvasText = query.get("canvas");
  // Round 1's phone link, `panel=website`, reads as a canvas of that one panel.
  const legacyText = query.get("panel");
  const legacyPanel = legacyText && isPanelId(legacyText) ? legacyText : null;
  const layout = useMemo((): CanvasLayout | null => {
    if (canvasText == null) return tile(legacyPanel ?? DEFAULT_PANEL);
    const parsed = parse(canvasText, isPanelId);
    return parsed === "invalid" ? tile(DEFAULT_PANEL) : parsed;
  }, [canvasText, legacyPanel]);

  // Any write drops `panel`: from then on the canvas carries the layout. A period change keeps the canvas text as
  // it is and, like a layout change, is one history entry.
  const go = useCallback(
    (next: { period?: ReportPeriod; layout?: CanvasLayout | null }) => {
      const nextLayout = next.layout === undefined ? (canvasText ?? (legacyPanel ? serialize(layout) : null)) : serialize(next.layout);
      navigate({ search: searchFor(next.period ?? period, nextLayout) });
    },
    [navigate, canvasText, legacyPanel, layout, period]
  );

  // The social report is a month's: a window fetches none, and reads the months it touches below.
  const {
    report, previousReport, comparison, historicalData,
    isLoading, error, isPending, isRefreshing,
    refreshingPlatform, refreshReport, refreshPlatform,
  } = useSocialMediaReport(windowed ? "" : clientId, year, month);
  // Rate of growth reads the social report's figures, so a refresh of it refetches growth too.
  const refetchGrowth = () => queryClient.invalidateQueries({ queryKey: growthKeys.all(clientId) });

  // A window's pips cover every source's span, each ending on its own settled day up to two weeks back, and each
  // chart keeps the pips inside its own span; Top posts rank the window to yesterday.
  const postsRange = useMemo((): DayRange | null => {
    if (!windowed) return null;
    return { since: addDays(today, -(windowDays(windowed) + 14)), until: today };
  }, [windowed, today]);
  const { reports: windowReports } = useWindowReports(clientId, postsRange);

  const periodLabel = windowed
    ? `Last ${WINDOW_PERIODS.find((p) => p.id === windowed)?.label ?? ""}`
    : `${MONTHS[month - 1]} ${year}`;
  const social = useMemo((): SocialReportState => {
    if (isLoading) return { status: "loading" };
    if (report) return { status: "ready", report, comparison, historicalData };
    if (isPending) {
      return { status: "missing", message: `No social report for ${periodLabel}.`, hint: "Reports are generated nightly. Refresh generates one now." };
    }
    if (error?.includes("No social media accounts connected")) {
      return { status: "missing", message: "No social media connected.", hint: "Connect Facebook or Instagram to see analytics." };
    }
    return { status: "missing", message: "Could not load the social report.", hint: error ?? "" };
  }, [isLoading, report, comparison, historicalData, isPending, error, periodLabel]);

  const postDays = useMemo(() => {
    if (postsRange) return postsByDay(windowReports, postsRange);
    return report ? postsByDay([report], monthRange(report.year, report.month)) : [];
  }, [postsRange, windowReports, report]);
  const topPosts = useMemo((): TopPost[] | null => {
    if (windowed) {
      const ranked = windowTopPosts(windowReports, rangeOf(addDays(today, -1), windowDays(windowed)));
      return ranked.length > 0 ? ranked : null;
    }
    return report ? report.topPosts : null;
  }, [windowed, windowReports, today, report]);

  const openPanels = panelsOf(layout).filter(isPanelId);
  const { layout: shown, hidden } = useMemo(() => visibleLayout(layout, cap), [layout, cap]);

  return {
    clientId,
    period,
    today,
    year,
    month,
    periodLabel,
    comparesPrevious: !windowed && previousReport != null,
    updatedAt: windowed ? null : report?._meta?.updatedAt ?? null,
    setPeriod: (next) => go({ period: next }),
    setMonth: (y, m) => go({ period: { kind: "month", year: y, month: m } }),
    social,
    hasReport: report != null,
    isRefreshing,
    refreshingPlatform,
    refreshReport: () => {
      void refreshReport().then(refetchGrowth);
    },
    refreshPlatform: (platform) => {
      void refreshPlatform(platform).then(refetchGrowth);
    },
    postDays,
    topPosts,

    tier,
    cap,
    layout,
    shown,
    openPanels,
    hiddenPanels: hidden.filter(isPanelId),
    canAdd: countTiles(layout) < cap,
    edgesFor: (panel) => edgesOfLayout(layout, panel, cap),
    open: (panel) => {
      if (layout == null) go({ layout: tile(panel) });
    },
    place: (panel, target, edge) => {
      const result = placeInLayout(layout, panel, { target, edge }, cap);
      if (result.ok) go({ layout: result.layout });
    },
    add: (panel) => {
      const result = autoPlace(layout, panel, cap);
      if (result.ok) go({ layout: result.layout });
    },
    replace: (target, panel) => go({ layout: replaceInLayout(layout, target, panel) }),
    close: (panel) => go({ layout: remove(layout, panel) }),
  };
}
